(() => {
  const ROOT_SELECTOR = '[data-sg-diagnostic]';

  const initDiagnostic = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.sgInitialized === 'true') return;

    const form = root.querySelector('form.sg-tool');
    const questions = Array.from(root.querySelectorAll('[data-sg-question]'));
    const progress = root.querySelector('[data-sg-progress]');
    const progressCount = root.querySelector('[data-sg-progress-count]');
    const progressBar = root.querySelector('[data-sg-progress-bar]');
    const result = root.querySelector('[data-sg-result]');
    const resultKicker = root.querySelector('[data-sg-result-kicker]');
    const resultTitle = root.querySelector('[data-sg-result-title]');
    const resultCopy = root.querySelector('[data-sg-result-copy]');
    const resultSummary = root.querySelector('[data-sg-result-summary]');
    const answerSummaryItems = Array.from(root.querySelectorAll('[data-sg-answer-summary]'));
    const interestRow = root.querySelector('[data-sg-interest-row]');
    const interestSummary = root.querySelector('[data-sg-interest-summary]');
    const resultActions = root.querySelector('[data-sg-result-actions]');
    const resultLink = root.querySelector('[data-sg-result-link]');
    const resultLinkLabel = root.querySelector('[data-sg-result-link-label]');
    const resetButton = root.querySelector('[data-sg-reset]');
    const announcer = root.querySelector('[data-sg-announcer]');
    const answerFields = Array.from(root.querySelectorAll('[data-sg-answer-field]'));
    const interestField = root.querySelector('[data-sg-interest-field]');
    const recommendationField = root.querySelector('[data-sg-recommendation-field]');
    const contactBody = root.querySelector('[data-sg-contact-body]');
    const submitSuccess = root.querySelector('[data-sg-submit-success]');
    const submitError = root.querySelector('[data-sg-submit-error]');

    if (
      !form ||
      !questions.length ||
      !progress ||
      !progressCount ||
      !progressBar ||
      !result ||
      !resultKicker ||
      !resultTitle ||
      !resultCopy ||
      !resultSummary ||
      answerSummaryItems.length !== questions.length ||
      !interestRow ||
      !interestSummary ||
      !resultActions ||
      !resultLink ||
      !resultLinkLabel ||
      !resetButton ||
      !announcer
    ) return;

    const storageKey = `solutello-growth-diagnostic:${window.location.pathname}:${form.id || 'contact'}`;
    const translateTemplate = (template, replacements = {}) => Object.entries(replacements)
      .reduce((message, [token, value]) => message.replaceAll(`__${token.toUpperCase()}__`, String(value)), template || '');

    const strings = {
      progress: root.dataset.sgProgressTemplate || '__COMPLETED__ of __TOTAL__ questions complete',
      resetAnnouncement: root.dataset.sgResetAnnouncement || 'Diagnostic reset. No answers selected.',
      sentKicker: root.dataset.sgSentKicker || 'DIAGNOSTIC SENT',
      reviewKicker: root.dataset.sgReviewKicker || 'REVIEW YOUR DIAGNOSTIC',
      sentTitle: root.dataset.sgSentTitle || 'Your diagnostic is with Solutello.',
      errorTitle: root.dataset.sgErrorTitle || 'One more step before we send it.',
      sentCopy: root.dataset.sgSentCopy || 'Your answers were submitted successfully. Choose a time to continue the conversation with Elias.',
      errorCopy: root.dataset.sgErrorCopy || 'Review the contact form error, then reselect any diagnostic answers that could not be restored.',
      pendingKicker: root.dataset.sgPendingKicker || 'COMPLETE THE THREE SIGNALS',
      pendingTitle: root.dataset.sgPendingTitle || 'Clarity starts with context.',
      pendingCopy: root.dataset.sgPendingCopy || 'Choose one answer in each section to reveal the most useful place to begin.',
      readyKicker: root.dataset.sgReadyKicker || 'RECOMMENDED STARTING POINT',
      sentReadyKicker: root.dataset.sgSentReadyKicker || 'DIAGNOSTIC SENT / RECOMMENDED STARTING POINT',
      completeAnnouncement: root.dataset.sgCompleteAnnouncement || 'All __TOTAL__ questions are complete. Your recommended starting point is __RECOMMENDATION__',
      incompleteAnnouncement: root.dataset.sgIncompleteAnnouncement || 'Answer all three diagnostic questions before sending.',
      questionFallback: root.dataset.sgQuestionFallback || 'Diagnostic question',
      bodyTitle: root.dataset.sgBodyTitle || 'Solutello Growth Diagnostic',
      bodyRecommended: root.dataset.sgBodyRecommended || 'Recommended starting point',
      bodyInterest: root.dataset.sgBodyInterest || 'Initial interest',
      bodySource: root.dataset.sgBodySource || 'Source page',
      notAvailable: root.dataset.sgNotAvailable || 'Not available'
    };

    const interestLabels = new Map([
      ['content-capture', root.dataset.sgInterestContentCapture || 'Content Capture Sprint'],
      ['performance-creative', root.dataset.sgInterestPerformanceCreative || 'Performance Creative Sprint'],
      ['content-engine', root.dataset.sgInterestContentEngine || 'Content Engine']
    ]);
    const recommendations = new Map();

    const normalizeInterest = (value) => {
      if (typeof value !== 'string') return '';
      return value.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 100);
    };

    const humanizeInterest = (value) => {
      const knownLabel = interestLabels.get(value.toLowerCase());
      if (knownLabel) return knownLabel;

      return value
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b[a-z]/g, (character) => character.toUpperCase());
    };

    let selectedInterest = normalizeInterest(new URLSearchParams(window.location.search).get('interest'));

    const getInterestDetails = () => {
      if (!selectedInterest) return null;
      const label = humanizeInterest(selectedInterest);
      return {
        value: selectedInterest,
        label,
        contactValue: label.toLowerCase() === selectedInterest.toLowerCase() ? label : `${label} (${selectedInterest})`
      };
    };

    root.querySelectorAll('[data-sg-recommendation]').forEach((source) => {
      const key = source.dataset.sgRecommendation;
      const title = source.querySelector('[data-sg-source-title]');
      const copy = source.querySelector('[data-sg-source-copy]');
      const link = source.querySelector('[data-sg-source-link]');

      if (key && title && copy && link) {
        recommendations.set(key, {
          title: title.textContent.trim(),
          copy: copy.textContent.trim(),
          linkLabel: link.textContent.trim(),
          url: source.dataset.url || '#'
        });
      }
    });

    const getAnswers = () => questions
      .map((question) => question.querySelector('input[type="radio"]:checked'))
      .filter(Boolean);

    const getAnswerDetails = (question, answer) => {
      const label = answer.closest('.sg-option')?.querySelector('.sg-option__label');
      const title = label?.querySelector('span')?.textContent.trim() || answer.value;
      const detail = label?.querySelector('small')?.textContent.trim() || '';
      const questionText = question.querySelector('legend')?.textContent
        .replace(/^\s*\d+\s*/, '')
        .trim() || strings.questionFallback;

      return {
        question: questionText,
        value: answer.value,
        title,
        detail,
        display: detail ? `${title} — ${detail}` : title
      };
    };

    const getRecommendationKey = (answers) => {
      const scores = { commerce: 0, content: 0, growth: 0 };

      answers.forEach((answer) => {
        const pillar = answer.dataset.sgPillar;
        const weight = Number.parseInt(answer.dataset.sgWeight || '0', 10);
        if (Object.prototype.hasOwnProperty.call(scores, pillar)) scores[pillar] += Number.isNaN(weight) ? 0 : weight;
      });

      const highestScore = Math.max(...Object.values(scores));
      const leaders = Object.keys(scores).filter((pillar) => scores[pillar] === highestScore);

      if (highestScore === 0 || leaders.length !== 1) return 'system';
      return leaders[0];
    };

    const saveAnswers = () => {
      const values = questions.map((question) => question.querySelector('input[type="radio"]:checked')?.value || null);

      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify({ answers: values, interest: selectedInterest }));
      } catch (_error) {
        // The diagnostic remains functional when storage is blocked.
      }
    };

    const restoreAnswers = () => {
      let storedState;

      try {
        storedState = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
      } catch (_error) {
        return;
      }

      const values = Array.isArray(storedState) ? storedState : storedState?.answers;
      if (!selectedInterest && !Array.isArray(storedState)) {
        selectedInterest = normalizeInterest(storedState?.interest);
      }

      if (!Array.isArray(values)) return;

      questions.forEach((question, index) => {
        if (question.querySelector('input[type="radio"]:checked') || !values[index]) return;
        const matchingInput = Array.from(question.querySelectorAll('input[type="radio"]'))
          .find((input) => input.value === values[index]);
        if (matchingInput) matchingInput.checked = true;
      });
    };

    const clearSavedAnswers = () => {
      try {
        if (selectedInterest) {
          window.sessionStorage.setItem(storageKey, JSON.stringify({
            answers: questions.map(() => null),
            interest: selectedInterest
          }));
        } else {
          window.sessionStorage.removeItem(storageKey);
        }
      } catch (_error) {
        // Nothing else is required when storage is blocked.
      }
    };

    const writeContactPayload = (details, recommendation) => {
      const interest = getInterestDetails();

      answerFields.forEach((field, index) => {
        field.value = details[index]?.display || '';
      });

      interestRow.hidden = !interest;
      interestSummary.textContent = interest?.label || '';
      if (interestField) interestField.value = interest?.contactValue || '';
      if (recommendationField) recommendationField.value = recommendation?.title || '';

      if (contactBody) {
        const lines = [
          strings.bodyTitle,
          `${strings.bodyRecommended}: ${recommendation?.title || strings.notAvailable}`,
          ...(interest ? [`${strings.bodyInterest}: ${interest.contactValue}`] : []),
          '',
          ...details.map((answer, index) => `${index + 1}. ${answer.question}: ${answer.display}`),
          '',
          `${strings.bodySource}: ${window.location.href.split('#')[0]}`
        ];
        contactBody.value = lines.join('\n');
      }
    };

    const update = ({ announce = true, reset = false } = {}) => {
      const answers = getAnswers();
      const completed = answers.length;
      const total = questions.length;
      const isReady = completed === total;
      const percentage = Math.round((completed / total) * 100);

      progress.setAttribute('aria-valuemax', String(total));
      progress.setAttribute('aria-valuenow', String(completed));
      progress.setAttribute('aria-valuetext', translateTemplate(strings.progress, { completed, total }));
      progressCount.textContent = `${completed} / ${total}`;
      progressBar.style.width = `${percentage}%`;
      resetButton.hidden = completed === 0 || Boolean(submitSuccess);
      saveAnswers();

      if (!isReady) {
        resultSummary.hidden = true;
        answerSummaryItems.forEach((item) => { item.textContent = ''; });
        writeContactPayload([], null);

        if (submitSuccess || submitError) {
          result.dataset.state = 'ready';
          resultKicker.textContent = submitSuccess ? strings.sentKicker : strings.reviewKicker;
          resultTitle.textContent = submitSuccess ? strings.sentTitle : strings.errorTitle;
          resultCopy.textContent = submitSuccess ? strings.sentCopy : strings.errorCopy;
          resultActions.hidden = false;
        } else {
          const wasPending = result.dataset.state === 'pending';
          result.dataset.state = 'pending';
          if (!wasPending) {
            resultKicker.textContent = strings.pendingKicker;
            resultTitle.textContent = strings.pendingTitle;
            resultCopy.textContent = strings.pendingCopy;
          }
          resultActions.hidden = true;
        }

        if (announce) {
          announcer.textContent = reset
            ? strings.resetAnnouncement
            : translateTemplate(strings.progress, { completed, total });
        }
        return;
      }

      const key = getRecommendationKey(answers);
      const recommendation = recommendations.get(key) || recommendations.get('system');
      if (!recommendation) return;

      const details = questions.map((question, index) => getAnswerDetails(question, answers[index]));
      details.forEach((answer, index) => { answerSummaryItems[index].textContent = answer.display; });
      resultSummary.hidden = false;
      writeContactPayload(details, recommendation);

      result.dataset.state = 'ready';
      resultKicker.textContent = submitSuccess ? strings.sentReadyKicker : strings.readyKicker;
      resultTitle.textContent = recommendation.title;
      resultCopy.textContent = recommendation.copy;
      resultLink.href = recommendation.url;
      resultLinkLabel.textContent = recommendation.linkLabel;
      resultActions.hidden = false;

      if (announce) {
        announcer.textContent = translateTemplate(strings.completeAnnouncement, {
          total,
          recommendation: recommendation.title
        });
      }
    };

    form.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.type === 'radio') update();
    });

    form.addEventListener('submit', (event) => {
      const answers = getAnswers();
      if (answers.length !== questions.length) {
        event.preventDefault();
        const firstIncomplete = questions.find((question) => !question.querySelector('input[type="radio"]:checked'));
        firstIncomplete?.querySelector('input[type="radio"]')?.focus();
        announcer.textContent = strings.incompleteAnnouncement;
        return;
      }

      update({ announce: false });
      saveAnswers();
    });

    form.addEventListener('reset', () => {
      clearSavedAnswers();
      window.setTimeout(() => update({ announce: true, reset: true }), 0);
    });

    restoreAnswers();
    root.dataset.sgInitialized = 'true';
    update({ announce: false });

    const statusToFocus = submitError || submitSuccess;
    if (statusToFocus) window.requestAnimationFrame(() => statusToFocus.focus());
  };

  const initAll = (scope = document) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) initDiagnostic(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initDiagnostic);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

(() => {
  const ROOT_SELECTOR = '[data-sg-diagnostic]';
  const ATTRIBUTION_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'gclid', 'fbclid', 'msclkid'
  ];
  const ATTRIBUTION_STORAGE_KEY = 'solutello:first-touch-attribution';

  const emit = (name, parameters = {}) => {
    const payload = {
      lead_type: 'growth_diagnostic',
      page_location: window.location.href.split('#')[0],
      page_referrer: document.referrer || undefined,
      language: document.documentElement.lang || undefined,
      ...parameters
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === '' || payload[key] == null) delete payload[key];
    });
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    }
  };

  const readAttribution = () => {
    let saved = {};
    try {
      saved = JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}') || {};
    } catch (_error) {
      saved = {};
    }

    const query = new URLSearchParams(window.location.search);
    const attribution = { ...saved };
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = query.get(key);
      if (!attribution[key] && value) attribution[key] = value;
    });
    if (!attribution.landing_page) attribution.landing_page = window.location.href.split('#')[0];
    if (!attribution.referrer && document.referrer) attribution.referrer = document.referrer;

    try {
      window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    } catch (_error) {
      // Attribution still populates the current form when storage is unavailable.
    }
    return attribution;
  };

  const initPipeline = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.sgPipelineInitialized === 'true') return;
    const form = root.querySelector('form.sg-tool');
    if (!form) return;

    root.dataset.sgPipelineInitialized = 'true';
    const attribution = readAttribution();
    root.querySelectorAll('[data-sg-attribution-field]').forEach((field) => {
      const key = field.dataset.sgAttributionField;
      field.value = attribution[key] || '';
    });

    const questions = Array.from(root.querySelectorAll('[data-sg-question]'));
    const success = root.querySelector('[data-sg-submit-success]');
    const recommendationField = root.querySelector('[data-sg-recommendation-field]');
    const leadMarker = 'solutello:growth-diagnostic-lead-tracked';
    let trackedStart = false;
    let trackedComplete = false;

    form.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      try {
        window.sessionStorage.removeItem(leadMarker);
      } catch (_error) {
        // A new submission can still be measured without storage.
      }

      const question = input.closest('[data-sg-question]');
      const questionIndex = questions.indexOf(question) + 1;
      if (!trackedStart) {
        trackedStart = true;
        emit('diagnostic_start', { question_index: questionIndex });
      }
      emit('diagnostic_answer', {
        question_index: questionIndex,
        answer: input.value
      });

      const complete = questions.length > 0 && questions.every((item) => item.querySelector('input[type="radio"]:checked'));
      if (complete && !trackedComplete) {
        trackedComplete = true;
        emit('diagnostic_complete', {
          diagnostic_recommendation: recommendationField?.value || undefined
        });
      }
    });

    form.addEventListener('submit', () => {
      const complete = questions.length > 0 && questions.every((item) => item.querySelector('input[type="radio"]:checked'));
      if (complete && form.checkValidity()) {
        emit('diagnostic_submit_attempt', {
          diagnostic_recommendation: recommendationField?.value || undefined
        });
      }
    });

    if (success) {
      let alreadyTracked = false;
      try {
        alreadyTracked = window.sessionStorage.getItem(leadMarker) === 'true';
      } catch (_error) {
        alreadyTracked = false;
      }
      if (!alreadyTracked) {
        emit('generate_lead', {
          diagnostic_recommendation: recommendationField?.value || undefined
        });
        try {
          window.sessionStorage.setItem(leadMarker, 'true');
        } catch (_error) {
          // The event was still emitted when storage is unavailable.
        }
      }
    }

    root.querySelectorAll('[data-sg-calendar-link]').forEach((link) => {
      link.addEventListener('click', () => {
        emit('calendar_click', {
          diagnostic_recommendation: recommendationField?.value || undefined,
          link_url: link.href
        });
      });
    });
  };

  const initAll = (scope = document) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) initPipeline(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initPipeline);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
